import type {
    CustomPagination,
    PaginatedPayload
} from '~/types';
import type {
    GenericApiErrorResponse
} from 'x1-base/types';
import { HttpStatusCodeType } from '~/types';
import type { FetchError } from 'ofetch';
import { omit as lo_omit } from 'es-toolkit';
import { isObject as lo_isObject } from 'es-toolkit/compat';
import { inRange as lo_inRange } from 'es-toolkit';
import { capitalize as lo_capitalize } from 'es-toolkit';
import { parseUrlStr } from 'x1-base/utils';
import { parseURL, parseQuery } from 'ufo';
import type { NuxtError } from '#app';
import type { RuntimeConfig } from 'nuxt/schema';

const strUnknownError = 'unknown error';
const genericErrorProps: Array<keyof GenericApiErrorResponse> = [ 'detail', 'error', 'message' ];

export function useApiUtils() {
    const $config = useRuntimeConfig();

    const apiBaseUrlObj: Partial<Nilish<URL>> = (() => parseUrlStr(`${$config.public.apiBaseURL}`))();

    const apiBasePath: string = (() => apiBaseUrlObj?.pathname || '')();

    function getErrorFromGenericApiErrorResponse(err: FetchError, capitalize: boolean = false): Error | void {
        let errorMsg = tryExtractObjFromError(err);

        if (errorMsg) {
            if (capitalize) {
                errorMsg = lo_capitalize(errorMsg);
            }

            return new Error(errorMsg);
        }
    }

    function getErrorObj(error: Error | unknown, altMessage = strUnknownError): Error {
        const errorMsg = (error as Error)?.message ?? altMessage;
        return Error(errorMsg);
    }

    function getNuxtErrorObj(
        error: NuxtError | Error | unknown,
        extOpts: Partial<NuxtError> = {},
        altMessage = strUnknownError
    ): NuxtError {
        if (lo_isObject(error) && ('statusCode' in error || 'statusMessage' in error)) {
            return Object.assign(error as NuxtError, extOpts);
        }

        const message = (error as Error)?.message ?? altMessage;
        return createError({ message });
    }

    function getPaginationQueryParams<T extends Record<string, string>>(url: string = ''): T {
        try {
            return parseQuery(parseURL(url || '').search);
        } catch (error) {
            return {} as T;
        }
    }

    function customToPaginatedPayload<T>(response: CustomPagination<T>): PaginatedPayload<T> {
        const payload = response.results;

        return {
            pagination: lo_omit(response, ['results']),
            data: payload
        };
    }

    function getNotImplementedError(): Error {
        return getErrorObj(new Error('Not implemented'));
    }

    function tryExtractObjFromError(errObj: Error | NuxtError): Nullable<string> {
        const errorObj = getDataFromError(errObj);

        if (errorObj) {
            for (const prop of genericErrorProps) {
                if (prop in errorObj) {
                    return errorObj[prop]?.toString() ?? null;
                }
            }
        }

        return null;
    }

    function getDataFromError(err: FetchError<unknown> | Error | unknown): Nilish<Record<string, unknown>> {
        if (lo_isObject(err) && 'data' in err) {
            return err.data as Record<string, unknown>;
        }
    }

    function isErrorOfStatusCode(errObj: Error | NuxtError, statusCode: number) {
        return ('statusCode' in errObj && errObj.statusCode === statusCode);
    }

    function getStatusTypeFromErrorObj(errObj: Error | NuxtError): HttpStatusCodeType {
        let status = HttpStatusCodeType.UNKNOWN;

        if ('statusCode' in errObj) {
            const code = errObj.statusCode;
            switch (true) {
                case lo_inRange(code, 100, 199):
                    return HttpStatusCodeType.INFORMATIONAL;
                case lo_inRange(code, 200, 299):
                    return HttpStatusCodeType.SUCCESSFUL;
                case lo_inRange(code, 300, 399):
                    return HttpStatusCodeType.REDIRECTION;
                case lo_inRange(code, 400, 499):
                    return HttpStatusCodeType.CLIENT;
                case lo_inRange(code, 500, 599):
                    return HttpStatusCodeType.SERVER;
            }
        }

        return status;
    }

    function getApiBaseUrl(apiName: keyof RuntimeConfig['public']['apiConfig']): Nullable<string> {
        const api = $config.public.apiConfig[apiName];

        if (api) {
            return import.meta.server ? api.basePath : api.browserBaseURL;
        }

        return null;
    }


    return {
        strUnknownError,
        apiBaseUrlObj,
        apiBasePath,
        getErrorFromGenericApiErrorResponse,
        getErrorObj,
        getNuxtErrorObj,
        getPaginationQueryParams,
        customToPaginatedPayload,
        tryExtractObjFromError,
        getNotImplementedError,
        isErrorOfStatusCode,
        getDataFromError,
        getStatusTypeFromErrorObj,
        getApiBaseUrl
    };
}
